import { canonicalIdPattern } from "./canonical-ids.js";
/**
 * Project memory (CWC plan B1/B3; owner decision 2026-10-13): the notes a project keeps across
 * runs, one row per path in `memory_entries`, which stays the record on every engine. On
 * Claude the rows are mirrored into one native memory store per project, mounted read-write
 * under MEMORY_MOUNT_DIR on every run; Foundry and OpenRouter reach the same rows through the
 * harness tools memory_read / memory_write. The limits below are Anthropic's (100 kB per
 * note, path-shaped names) so a note written on any engine mounts on Claude unchanged.
 */
/** One note's text cap (UTF-8 bytes): Anthropic's per-memory maximum. */
export const MEMORY_TEXT_MAX_BYTES = 102_400;
/** Notes per project. */
export const MEMORY_KEYS_MAX = 200;
/** A note's path cap (UTF-8 bytes). */
export const MEMORY_PATH_MAX_BYTES = 512;
/** The one store every project has on Claude; its name is the mount's last directory. */
export const MEMORY_STORE_NAME = "project-memory";
/** Where the store mounts in a Claude session; a note at path `notes/x.md` is read at `<dir>/notes/x.md`. */
export const MEMORY_MOUNT_DIR = `/mnt/memory/${MEMORY_STORE_NAME}`;
/** The canonical memory id on the wire: `memory:<uuid>`. */
export const CANONICAL_MEMORY_ID = canonicalIdPattern("memory");
const utf8Bytes = (text) => new TextEncoder().encode(text).length;
/**
 * Why a path cannot name a note, or null when it can: relative (no leading slash), up to 512
 * bytes, no `.` segment and no `..` anywhere (migration 029's memory_entries_key_check refuses
 * `..` inside a segment too), no empty segments, no control characters (C0, DEL, and the C1
 * range and line separators a UTF-8 locale's `[[:cntrl:]]` matches). The database holds the
 * same rule, for any other writer: a path accepted here is one it stores.
 */
export function memoryPathIssue(path) {
    if (path.length === 0)
        return "empty";
    if (utf8Bytes(path) > MEMORY_PATH_MAX_BYTES)
        return "too_long";
    if (path.startsWith("/"))
        return "leading_slash";
    if (path.endsWith("/"))
        return "trailing_slash";
    // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are exactly what is refused
    if (/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/.test(path))
        return "control_character";
    if (path.includes(".."))
        return "dot_segment";
    for (const segment of path.split("/")) {
        if (segment === "")
            return "empty_segment";
        if (segment === ".")
            return "dot_segment";
    }
    return null;
}
/**
 * Whether two paths cannot both be notes: one is a directory of the other (`notes` and
 * `notes/x.md`), which a filesystem mount cannot hold at once (Anthropic answers 409).
 */
export function memoryPathOverlap(a, b) {
    if (a === b)
        return false;
    return a.startsWith(`${b}/`) || b.startsWith(`${a}/`);
}
/** Where a note mounts in a Claude session. */
export function memoryMountPath(path) {
    return `${MEMORY_MOUNT_DIR}/${path}`;
}
/*
 * Memory scopes (owner decision 29): a project groups its sessions' memory into named
 * scopes, each a directory of files mounted at /mnt/memory/<slug>/ and, on Claude, its own
 * memory store. A session joins the scopes its template's overlay names (`memory.scopes`)
 * or its start input overrides; one that names none joins the default scope, the store
 * every project has had since #208.
 */
/** The scope every project has, whose name is #208's store name and mount directory. */
export const DEFAULT_MEMORY_SCOPE = MEMORY_STORE_NAME;
/** How many scopes one project may hold. */
export const MEMORY_SCOPES_PER_PROJECT_MAX = 50;
/** How many scopes one session may join. */
export const MEMORY_SCOPES_PER_SESSION_MAX = 8;
/** A scope's slug: its mount directory's name and its Claude store's name. */
export const MEMORY_SCOPE_SLUG = /^[a-z0-9][a-z0-9-]{0,62}$/;
/** Where a scope mounts in a session. */
export function memoryScopeMountDir(scope) {
    return `/mnt/memory/${scope}`;
}
/**
 * The scopes an overlay (parsed YAML of any shape) names under `memory.scopes`, or null
 * when it names none; the checker validates the shape, so anything else reads as none.
 */
export function overlayMemoryScopes(overlay) {
    if (!overlay || typeof overlay !== "object" || Array.isArray(overlay))
        return null;
    const memory = overlay.memory;
    if (!memory || typeof memory !== "object" || Array.isArray(memory))
        return null;
    const scopes = memory.scopes;
    if (!Array.isArray(scopes) || scopes.length === 0)
        return null;
    return scopes.filter((s) => typeof s === "string");
}
//# sourceMappingURL=memory.js.map